import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import SectionTitle from '@/components/base/SectionTitle';
import VideoCard from '@/components/feature/VideoCard';
import PlayerPanel from './components/PlayerPanel';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useFavorites } from '@/hooks/useFavorites';
import { useHistory } from '@/hooks/useHistory';
import { getRelatedVideos, getVideoById } from '@/utils/media';
import { videoTypeLabels } from '@/mocks/videos';

export default function DetailPage() {
  const { id = '' } = useParams();
  const video = getVideoById(id);

  useDocumentMeta(
    video ? `【${video.title}】- 影视详情 - 黄牛视频` : '作品不存在 - 黄牛视频',
    video
      ? `${video.title}（${video.year}）${video.genres.join('、')}，导演${video.director}，主演${video.actors.join('、')}。${video.synopsis.slice(0, 70)}`
      : '该作品暂无信息或已下架，可返回首页浏览其他影视内容。',
    video ? `${video.title},${video.title}详情,${video.genres.join(',')},黄牛视频` : '黄牛视频',
  );

  const { isFavorite, toggleFavorite } = useFavorites();
  const { addHistory } = useHistory();
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (video) addHistory(video.id);
  }, [video, addHistory]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => (video ? getRelatedVideos(video, 6) : []), [video]);

  if (!video) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-file-unknow-line"
          title="作品暂无信息或已下架"
          description="该影视作品可能已下架或链接失效，你可以返回首页浏览更多优质内容。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const favorite = isFavorite(video.id);

  const handleFavorite = () => {
    toggleFavorite(video.id);
    setToast(favorite ? '已取消收藏' : '已加入收藏');
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('链接已复制，快去分享给朋友吧');
    } catch {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      try {
        document.execCommand('copy');
        setToast('链接已复制，快去分享给朋友吧');
      } catch {
        setToast(`复制失败，可手动复制：${url}`);
      }
      document.body.removeChild(input);
    }
  };

  const infoRows = [
    { label: '导演', value: video.director },
    { label: '主演', value: video.actors.join(' / ') },
    { label: '类型', value: video.genres.join(' / ') },
    { label: '地区', value: video.region },
    { label: '年份', value: String(video.year) },
    { label: '状态', value: video.status },
  ];

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: videoTypeLabels[video.type], to: `/category/${video.type}` },
          { label: video.title },
        ]}
      />

      <div className="relative mb-8 overflow-hidden rounded-lg border border-background-200">
        <div className="absolute inset-0">
          <img
            src={video.backdrop}
            alt={`${video.title} 背景图`}
            title={`${video.title} 背景图`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/85 to-background-50/60" />
        </div>
        <div className="relative flex flex-col gap-6 p-4 md:flex-row md:p-8">
          <div className="w-40 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-100 md:w-56">
            <img
              src={video.poster}
              alt={`${video.title} 海报`}
              title={`${video.title} 高清海报`}
              className="aspect-[2/3] h-full w-full object-cover object-top"
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                {videoTypeLabels[video.type]}
              </span>
              <span className="flex items-center gap-1 text-sm font-semibold text-accent-500">
                <i className="ri-star-fill" />
                {video.rating.toFixed(1)} 分
              </span>
              <span className="text-sm text-foreground-400">更新于 {video.updatedAt}</span>
            </div>
            <h1 className="mt-3 text-2xl font-bold text-foreground-50 md:text-4xl">{video.title}</h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-300 md:text-base">
              {video.synopsis}
            </p>
            <div className="mt-auto flex flex-wrap items-center gap-3 pt-6">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                  favorite
                    ? 'bg-accent-500 text-background-50'
                    : 'border border-background-300 text-foreground-100 hover:border-accent-500 hover:text-accent-500'
                }`}
              >
                <i className={favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                {favorite ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex items-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-100 transition-colors hover:border-primary-400 hover:text-primary-500"
              >
                <i className="ri-share-forward-line" />
                分享链接
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PlayerPanel video={video} onDemoNotice={setToast} />
        </div>
        <aside className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
          <h2 className="mb-4 text-lg font-semibold text-foreground-50">作品信息</h2>
          <dl className="space-y-4">
            {infoRows.map((row) => (
              <div key={row.label} className="flex gap-3 text-sm">
                <dt className="w-12 shrink-0 text-foreground-500">{row.label}</dt>
                <dd className="flex-1 text-foreground-200">{row.value}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </div>

      <section className="mt-12">
        <SectionTitle
          title="相关推荐"
          subtitle="根据类型与题材为你精选"
          icon="ri-magic-line"
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {related.map((item) => (
            <VideoCard key={item.id} video={item} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 animate-fade-up rounded-md border border-background-200 bg-background-100 px-5 py-3 text-sm text-foreground-100 shadow-lg">
          <span className="flex items-center gap-2">
            <i className="ri-information-line text-primary-500" />
            {toast}
          </span>
        </div>
      )}
    </div>
  );
}